import { isAddress, type Address } from "viem";
import { privateKeyToAccount, type PrivateKeyAccount } from "viem/accounts";

const MAX_WALLETS = 2_000;
const PRIVATE_KEY = /^(?:0x)?[0-9a-fA-F]{64}$/;

export interface ImportedWallet {
  readonly address: Address;
  readonly duplicate: boolean;
}

export interface ImportIssue {
  readonly line: number;
  readonly reason: string;
}

export interface ImportResult {
  readonly wallets: readonly ImportedWallet[];
  readonly issues: readonly ImportIssue[];
}

/**
 * Session-only signer vault. Private keys are converted to signer closures and
 * never returned to React state, storage, exports, logs, or the DOM after import.
 */
export class EphemeralKeyVault {
  readonly #accounts = new Map<string, PrivateKeyAccount>();

  importText(value: string): ImportResult {
    const wallets: ImportedWallet[] = [];
    const issues: ImportIssue[] = [];
    const lines = value.split(/\r?\n/);

    for (const [index, rawLine] of lines.entries()) {
      const line = rawLine.trim();
      if (line === "" || line.startsWith("#")) continue;
      if (!PRIVATE_KEY.test(line)) {
        issues.push({
          line: index + 1,
          reason: "每行仅支持一个 64 位十六进制私钥",
        });
        continue;
      }

      if (this.#accounts.size >= MAX_WALLETS) {
        issues.push({ line: index + 1, reason: `单次会话最多导入 ${MAX_WALLETS} 个钱包` });
        continue;
      }

      try {
        const account = privateKeyToAccount(
          (line.startsWith("0x") ? line : `0x${line}`) as `0x${string}`,
        );
        if (!isAddress(account.address)) throw new Error("invalid derived address");
        const normalized = account.address.toLowerCase();
        const duplicate = this.#accounts.has(normalized);
        if (!duplicate) this.#accounts.set(normalized, account);

        wallets.push({ address: account.address, duplicate });
      } catch {
        issues.push({ line: index + 1, reason: "私钥无法派生有效钱包" });
      }
    }

    return { wallets, issues };
  }

  accountFor(address: Address): PrivateKeyAccount | undefined {
    return this.#accounts.get(address.toLowerCase());
  }

  has(address: Address): boolean {
    return this.#accounts.has(address.toLowerCase());
  }

  get size(): number {
    return this.#accounts.size;
  }

  clear(): void {
    this.#accounts.clear();
  }
}
